import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import { AppContext } from "App";
import Button from "components/base/Button";
import { InlineSpinner } from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import ComponentsView from "components/shared/ComponentsView";
import { useModals } from "ModalProvider";
import { useContext, useEffect, useRef, useState } from "react";
import { SAVE_PROJECT } from "../projects/graphql";
import { GTMProjectContext } from "./GTMProjectContext";
import PackagingOptionForm from "./PackagingOptionForm";
import ProductPackagingOptionView from "./ProductPackagingOptionView";

const defaultValues = {
  bomQty: 1,
  orderQty: 3000,
  packagings: [],
};

const GTMCostView = () => {
  const { user } = useContext(AppContext);
  const { originalGTMProject, setAutoSaved } = useContext(GTMProjectContext);
  const [saveProject, saveProjectRes] = useMutation(SAVE_PROJECT, {
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  const readOnly =
    user.id !== originalGTMProject.startUpProject.owner.id &&
    user.email !== "charles@waboba.com" &&
    user.email !== "alex.c@waboba.com" &&
    user.email !== "stella@waboba.com";

  const originalData = JSON.parse(originalGTMProject.startUpProject.data);

  const [data, setData] = useState({
    ...defaultValues,
    bomQty: originalData.bomQty,
    orderQty: originalData.orderQty,
    components: originalData.components || [],
    packagings: originalData.packagings || [],
  });

  useEffect(() => {
    const nextData = JSON.parse(originalGTMProject.startUpProject.data || "{}");
    setData({
      ...defaultValues,
      bomQty: nextData.bomQty,
      orderQty: nextData.orderQty,
      components: nextData.components || [],
      packagings: nextData.packagings || [],
    });
  }, [originalGTMProject.startUpProject.data]);

  const timer = useRef(null);

  function onChange(dataValues) {
    const nextData = dataValues ? { ...data, ...dataValues } : data;
    if (dataValues) setData(nextData);

    // // debounce saves: clear any pending timer and schedule a new save
    if (timer.current) clearTimeout(timer.current);

    timer.current = setTimeout(() => {
      saveProject({
        variables: {
          id: originalGTMProject.startUpProject.id,
          data: JSON.stringify({ ...originalData, ...nextData }, " ", 2),
        },
        onCompleted() {
          setAutoSaved(true);
        },
      });
    }, 1000);
  }

  const incNumber =
    data.productType === "NEW_CLASSIC_LINE_PRODUCT" ? 0.7 : 0.62;

  const componentsWithCurrency =
    data.components?.map((i) => ({
      ...i,
      prices: i.prices.map((p) => ({
        ...p,
        currency: i.currency || "CNY",
      })),
    })) || [];

  const modal = useModals();

  function addPackagingCost() {
    if (!originalData.components || originalData.components.length === 0) {
      window.alert("Please add components before adding packaging options.");
      return;
    }

    modal.present({
      title: "Add Packaging Option",
      children: (
        <PackagingOptionForm
          components={data.components}
          onSave={onSavePackaging}
        />
      ),
    });
  }

  function editPackaging(packagingInfo) {
    modal.present({
      title: "Edit Packaging",
      children: (
        <PackagingOptionForm
          components={data.components}
          initialValues={packagingInfo}
          onSave={onSavePackaging}
          onDelete={onDeletePackaging}
        />
      ),
    });
  }

  function onSavePackaging(packagingInfo) {
    modal.hide();
    const pkg = data.packagings.find((p) => p.id === packagingInfo.id);
    let newPackagings = [];
    if (pkg) {
      newPackagings = data.packagings.map((p) =>
        p.id === packagingInfo.id ? packagingInfo : p,
      );
    } else {
      newPackagings = [...data.packagings, packagingInfo];
    }
    setData({ ...data, packagings: newPackagings });
    onChange({ packagings: newPackagings });
  }

  function onDeletePackaging(id) {
    modal.hide();
    const newPackagings = data.packagings.filter((p) => p.id !== id);
    setData({ ...data, packagings: newPackagings });
    onChange({ packagings: newPackagings });
  }

  function onChangePackingOption(index, newValues) {
    const newPackagings = data.packagings.map((p, i) =>
      i === index ? { ...p, ...newValues } : p,
    );
    setData({ ...data, packagings: newPackagings });
    onChange({ packagings: newPackagings });
  }

  return (
    <div className="mt-4">
      <div className="flex items-center space-x-3">
        <h6>Price Breakdown</h6>

        <div
          className={`relative ${saveProjectRes.loading ? "opacity-100" : " opacity-0"}`}
        >
          <InlineSpinner size={18} />
        </div>
      </div>
      <div className="mt-2">
        <ComponentsView
          readOnly={readOnly}
          components={componentsWithCurrency}
          onChange={(newValues) => onChange(newValues)}
          bomQty={data.bomQty}
          orderQty={data.orderQty}
          fixedComponentQty
        />
      </div>

      <hr />
      {data.packagings.length > 0 && (
        <div className="mt-8 space-y-8">
          {data.packagings.map((i, index) => (
            <ProductPackagingOptionView
              key={index}
              components={componentsWithCurrency}
              index={index}
              packagingInfo={i}
              editPackaging={editPackaging}
              incNumber={incNumber}
              onChange={(newComponents) =>
                onChangePackingOption(index, newComponents)
              }
            />
          ))}
        </div>
      )}
      <div className="mt-8 space-y-2">
        <Button onClick={addPackagingCost}>+ Add Packaging Option</Button>
      </div>
      <hr />
    </div>
  );
};

export default GTMCostView;
